import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight, Layers } from "lucide-react";

const TYPES = ["all", "colorway", "print", "fabric"];
const MAX_SELECT = 6;

export default function BulkStudio() {
  const [designs, setDesigns] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [styles, setStyles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [designId, setDesignId] = useState("");
  const [styleId, setStyleId] = useState("");
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
    api.get("/styles").then((r) => setStyles(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "all" ? materials : materials.filter((m) => m.type === filter);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((s) => s !== id));
    else if (selected.length < MAX_SELECT) setSelected([...selected, id]);
    else toast.warning(`Maximum ${MAX_SELECT} materials per batch`);
  };

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const body = { design_id: designId, material_ids: selected };
      if (styleId) body.style_id = styleId;
      const { data } = await api.post("/generate/bulk", body, { timeout: 300000 });
      setResult(data);
      if (data.failed > 0) toast.warning(`${data.new_versions.length} variations created, ${data.failed} failed`);
      else toast.success(`${data.new_versions.length} colorway variations created`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="h-16 border-b border-zinc-200 bg-white flex items-center px-8 sticky top-0 z-30">
        <div>
          <h1 className="font-heading font-semibold text-lg leading-tight">Bulk Colorways</h1>
          <p className="text-xs text-zinc-400">Generate up to {MAX_SELECT} colorway variations of a design in one batch</p>
        </div>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[calc(100vh-4rem)]">
        <div className="lg:col-span-2 border-r border-zinc-200 bg-white p-8 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Base design</label>
            <div className="mt-2 grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
              {designs.length === 0 && <p className="text-xs text-zinc-400 col-span-3">No designs yet — create one in Sketch to Render.</p>}
              {designs.map((d) => (
                <button key={d.id} data-testid={`bulk-design-${d.id}`} onClick={() => setDesignId(d.id)}
                  className={`border rounded-md overflow-hidden text-left transition-colors ${designId === d.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
                  <img src={imgUrl(d.cover_image)} alt={d.title} className="w-full h-20 object-cover" />
                  <p className="text-[10px] p-1.5 truncate">{d.title}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Materials ({selected.length}/{MAX_SELECT})</label>
              <div className="flex gap-1">
                {TYPES.map((t) => (
                  <button key={t} data-testid={`bulk-filter-${t}`} onClick={() => setFilter(t)}
                    className={`px-2.5 py-1 rounded text-[11px] capitalize transition-colors ${filter === t ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
              {filtered.map((m) => (
                <button key={m.id} data-testid={`bulk-swatch-${m.id}`} onClick={() => toggle(m.id)} title={m.name}
                  className={`relative border rounded-md overflow-hidden transition-colors ${selected.includes(m.id) ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
                  {selected.includes(m.id) && (
                    <span className="absolute top-1 right-1 z-10 bg-zinc-950 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                      {selected.indexOf(m.id) + 1}
                    </span>
                  )}
                  <img src={imgUrl(m.image_url)} alt={m.name} className="w-full h-16 object-cover" />
                  <p className="text-[10px] p-1 truncate">{m.name}</p>
                </button>
              ))}
            </div>
          </div>
          {styles.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Brand style (optional)</label>
              <select value={styleId} onChange={(e) => setStyleId(e.target.value)}
                className="mt-2 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">No brand style</option>
                {styles.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <button data-testid="bulk-generate-button" onClick={generate} disabled={!designId || selected.length === 0 || generating}
            className="w-full flex items-center justify-center gap-2 bg-zinc-950 text-white py-3.5 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
            {generating ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating {selected.length} variations…</>
            ) : (
              <><Sparkles size={16} /> Generate {selected.length || ""} variation{selected.length !== 1 ? "s" : ""}</>
            )}
          </button>
          {generating && <p className="text-xs text-zinc-400 text-center">Variations run in parallel — usually 30–90 seconds total.</p>}
        </div>
        <div className="lg:col-span-3 p-8">
          {result ? (
            <div className="fade-up">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Batch result</p>
                  <h2 className="font-heading font-semibold" data-testid="bulk-result-title">{result.design.title} — {result.new_versions.length} new colorways</h2>
                </div>
                <Link to={`/designs/${result.design.id}`} data-testid="bulk-open-design-link"
                  className="inline-flex items-center gap-1.5 text-sm font-medium border border-zinc-300 px-4 py-2 rounded-md hover:bg-zinc-100 transition-colors">
                  Open design <ArrowUpRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {result.new_versions.map((v) => (
                  <div key={v.id} data-testid={`bulk-result-${v.id}`} className="border border-zinc-200 bg-white rounded-md overflow-hidden">
                    <img src={imgUrl(v.image)} alt={v.prompt} className="w-full h-64 object-cover" />
                    <p className="text-xs p-2.5 text-zinc-600 truncate">{v.prompt.replace("Bulk colorway: ", "")}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[400px] border-2 border-dashed border-zinc-200 rounded-md flex flex-col items-center justify-center text-zinc-400">
              <Layers size={28} className="mb-4" />
              <p className="text-sm">Select a design and up to {MAX_SELECT} materials — variations appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
